import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import Logo from '../../components/Logo.jsx'
import { getPublicWhatsappConnect, getPublicWhatsappPairingCode } from '../../db/api.js'
import { friendlyWaError } from '../../lib/waErrors.js'

/** QR da Evolution/Baileys expira bem antes de 40s; poll mais curto evita
 * mostrar código já morto pro cliente escanear. */
const POLL_MS = 12_000
/** TTL assumido do QR (issue #82): passado isso sem uma renovação confirmada,
 * o código na tela é invalidado visualmente em vez de deixado como se ainda
 * fosse válido — evita o cliente escanear um QR morto e ler texto cru. */
const QR_TTL_MS = 20_000
/** Sem conectar depois disso, mostra a dica de bloqueio temporário + pairing code. */
const LONG_WAIT_MS = 90_000

const STATUS_LABEL = {
  connecting: 'Aguardando leitura do QR Code',
  open: 'Conectado',
  close: 'Desconectado',
}

// Paleta local — página pública não herda o tema do app (que pode ser escuro).
const C = {
  bg: '#FFFFFF',
  text: '#0f172a',
  sub: '#64748b',
  border: '#e7e9ee',
  accent: '#4f46e5',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#b45309',
  amberBg: '#fffbeb',
}

/**
 * Página PÚBLICA de conexão do WhatsApp (sem login).
 *
 * Acessada por link com token opaco: /conectar-whatsapp/:token.
 * Só consome o endpoint público (state + qrcode). Quando conecta, o token é
 * invalidado no backend e o link para de funcionar.
 *
 * Issue #82: o QR nunca fica vencido na tela (invalidação visual pelo TTL
 * conhecido), uma falha de poll passageira não derruba mais o fluxo (só um
 * 404 — token morto — para de vez), e há um pairing code como alternativa
 * pra quando o QR falha ao escanear.
 */
export default function PublicConnect() {
  const { token } = useParams()
  const [state, setState] = useState(null)
  const [qr, setQr] = useState(null)
  const [qrIssuedAt, setQrIssuedAt] = useState(0)
  const [qrStale, setQrStale] = useState(false)
  const [fatalError, setFatalError] = useState('')
  const [pollWarning, setPollWarning] = useState('')
  const [longWait, setLongWait] = useState(false)
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState('qr') // 'qr' | 'pairing'
  const timerRef = useRef(null)
  const startedAtRef = useRef(Date.now())

  const poll = useCallback(async () => {
    try {
      const r = await getPublicWhatsappConnect(token)
      setState(r.state)
      if (r.qrcode) {
        setQr(r.qrcode)
        setQrIssuedAt(Date.now())
      }
      setFatalError('')
      setPollWarning('')
      if (r.state === 'open' && timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    } catch (e) {
      if (e.status === 404) {
        // Link morto (usado/revogado) — não adianta continuar tentando.
        setFatalError(e.message || 'Link inválido ou já utilizado.')
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      } else {
        // Falha passageira (Evolution lenta/fora do ar, rede) — mantém o que
        // já está na tela e segue tentando; só avisa, não trava o fluxo.
        setPollWarning('Não foi possível atualizar agora. Tentando de novo…')
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    poll()
    timerRef.current = setInterval(poll, POLL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [poll])

  // Invalida visualmente o QR passado o TTL — nunca deixa um código morto
  // parecendo válido na tela enquanto espera o próximo poll confirmar.
  useEffect(() => {
    if (!qrIssuedAt) return
    const check = () => setQrStale(Date.now() - qrIssuedAt > QR_TTL_MS)
    check()
    const t = setInterval(check, 1000)
    return () => clearInterval(t)
  }, [qrIssuedAt])

  // Dica de bloqueio temporário / pairing code depois de um tempo sem conectar.
  useEffect(() => {
    if (state === 'open') return
    const t = setInterval(() => {
      if (Date.now() - startedAtRef.current > LONG_WAIT_MS) setLongWait(true)
    }, 5000)
    return () => clearInterval(t)
  }, [state])

  const qrSrc = qr ? (qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`) : null

  return (
    <div style={S.page}>
      {/* Keyframes locais p/ spinner + fade-in do card */}
      <style>{KEYFRAMES}</style>

      {/* Identidade visual — canto superior esquerdo */}
      <header style={S.brand}>
        <Logo height={16} />
      </header>

      <main style={S.main}>
        <section style={S.card}>
          <h1 style={S.title}>Conecte seu WhatsApp</h1>
          <p style={S.subtitle}>
            {mode === 'pairing'
              ? 'Use o código de pareamento no seu WhatsApp para finalizar a conexão.'
              : 'Escaneie o QR Code abaixo com seu WhatsApp para finalizar a conexão.'}
          </p>

          <div style={S.body}>
            {renderBody({
              loading, fatalError, state, qrSrc, qrStale, mode, setMode, token,
              pollWarning, longWait,
            })}
          </div>
        </section>

        <p style={S.footer}>Protegido por TITAM AI · conexão criptografada</p>
      </main>
    </div>
  )
}

/** Corpo dinâmico conforme o estado da conexão. */
function renderBody({
  loading, fatalError, state, qrSrc, qrStale, mode, setMode, token, pollWarning, longWait,
}) {
  if (loading) {
    return (
      <div style={S.stateWrap}>
        <span style={S.spinner} />
        <p style={S.stateText}>Gerando QR Code…</p>
      </div>
    )
  }

  if (fatalError) {
    return (
      <div style={S.stateWrap}>
        <div style={{ ...S.badgeCircle, background: '#fef2f2', color: C.red }}>!</div>
        <p style={{ ...S.stateTitle, color: C.red }}>Não foi possível abrir o link</p>
        <p style={S.stateText}>{fatalError}</p>
      </div>
    )
  }

  if (state === 'open') {
    return (
      <div style={S.stateWrap}>
        <div style={{ ...S.badgeCircle, background: '#f0fdf4', color: C.green }}>✓</div>
        <p style={{ ...S.stateTitle, color: C.green }}>WhatsApp conectado com sucesso!</p>
        <p style={S.stateText}>Você já pode fechar esta página.</p>
      </div>
    )
  }

  return (
    <>
      <div style={S.modeTabs}>
        <button
          type="button"
          style={{ ...S.modeTab, ...(mode === 'qr' ? S.modeTabActive : null) }}
          onClick={() => setMode('qr')}
        >
          QR Code
        </button>
        <button
          type="button"
          style={{ ...S.modeTab, ...(mode === 'pairing' ? S.modeTabActive : null) }}
          onClick={() => setMode('pairing')}
        >
          Código de pareamento
        </button>
      </div>

      {mode === 'pairing' ? (
        <PairingCodePanel token={token} />
      ) : (
        <>
          <div style={S.qrFrame}>
            {qrSrc ? (
              <>
                <img
                  src={qrSrc}
                  alt="QR Code do WhatsApp"
                  style={{ ...S.qrImg, ...(qrStale ? S.qrImgStale : null) }}
                />
                {qrStale && (
                  <div style={S.qrOverlay}>
                    <span style={S.spinner} />
                    <span style={S.stateText}>Atualizando código…</span>
                  </div>
                )}
              </>
            ) : (
              <div style={S.stateWrap}>
                <span style={S.spinner} />
                <p style={S.stateText}>Gerando QR Code…</p>
              </div>
            )}
          </div>
          <p style={S.hint}>
            No WhatsApp: <strong>Aparelhos conectados</strong> &gt; <strong>Conectar aparelho</strong>.
            O código se renova sozinho antes de expirar.
          </p>
        </>
      )}

      <div style={S.statusPill}>
        <span style={S.dot} />
        {STATUS_LABEL[state] || 'Carregando…'}
      </div>

      {pollWarning && <p style={S.warningText}>{pollWarning}</p>}

      {longWait && (
        <div style={S.longWaitBox}>
          Ainda não conectou? Depois de várias tentativas o próprio WhatsApp pode bloquear
          novos pareamentos por um tempo — vale esperar alguns minutos antes de tentar de
          novo, ou usar o <strong>código de pareamento</strong> acima como alternativa.
        </div>
      )}
    </>
  )
}

/** Painel do pairing code: pede o telefone e mostra o código de 8 dígitos. */
function PairingCodePanel({ token }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function generate() {
    setError('')
    setCode('')
    setBusy(true)
    try {
      const r = await getPublicWhatsappPairingCode(token, phone)
      setCode(r.pairing_code)
    } catch (e) {
      // O backend já devolve mensagem amigável pra esse endpoint (issue #82);
      // só cai no tradutor genérico se por algum motivo vier vazia.
      setError(e.message || friendlyWaError())
    } finally {
      setBusy(false)
    }
  }

  if (code) {
    return (
      <div style={S.stateWrap}>
        <div style={S.pairingCode}>{code}</div>
        <p style={S.hint}>
          No WhatsApp: <strong>Aparelhos conectados</strong> &gt; <strong>Conectar aparelho</strong>
          &gt; <strong>Conectar com número de telefone</strong> e digite o código acima.
        </p>
        <button type="button" style={S.linkBtn} onClick={() => setCode('')}>
          Gerar outro código
        </button>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', maxWidth: 300 }}>
      <label style={S.fieldLabel} htmlFor="wa-phone">Número do WhatsApp (com DDI e DDD)</label>
      <input
        id="wa-phone"
        style={S.input}
        placeholder="5511999999999"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        inputMode="numeric"
      />
      {error && <p style={{ ...S.stateText, color: C.red, marginTop: 8 }}>{error}</p>}
      <button
        type="button"
        style={S.primaryBtn}
        onClick={generate}
        disabled={busy || phone.replace(/\D/g, '').length < 8}
      >
        {busy ? 'Gerando…' : 'Gerar código'}
      </button>
    </div>
  )
}

const KEYFRAMES = `
@keyframes deebs-spin { to { transform: rotate(360deg); } }
@keyframes deebs-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes deebs-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
`

const S = {
  page: {
    minHeight: '100vh',
    background: C.bg,
    color: C.text,
    display: 'flex',
    flexDirection: 'column',
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  brand: {
    position: 'absolute',
    top: 24,
    left: 28,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: '#fff',
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: 'clamp(24px, 5vw, 40px)',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(15, 23, 42, 0.08)',
    animation: 'deebs-fade .35s ease',
  },
  title: { margin: 0, fontSize: 23, fontWeight: 700, letterSpacing: '-0.02em' },
  subtitle: {
    margin: '8px auto 0',
    maxWidth: 320,
    fontSize: 14,
    lineHeight: 1.55,
    color: C.sub,
  },
  body: { marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  modeTabs: {
    display: 'flex',
    gap: 4,
    padding: 4,
    background: '#f8fafc',
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    marginBottom: 20,
  },
  modeTab: {
    border: 'none',
    background: 'transparent',
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: C.sub,
    cursor: 'pointer',
  },
  modeTabActive: {
    background: '#fff',
    color: C.text,
    boxShadow: '0 1px 3px rgba(15,23,42,.08)',
  },
  qrFrame: {
    position: 'relative',
    width: 'min(280px, 72vw)',
    height: 'min(280px, 72vw)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 16,
    border: `1px solid ${C.border}`,
    background: '#fff',
    boxShadow: 'inset 0 0 0 4px #fff, 0 4px 16px rgba(15,23,42,.06)',
  },
  qrImg: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 },
  qrImgStale: { filter: 'blur(3px) grayscale(60%)', opacity: 0.5 },
  qrOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    background: 'rgba(255,255,255,.55)',
    borderRadius: 16,
  },
  hint: {
    margin: '18px auto 0',
    maxWidth: 320,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: C.sub,
  },
  statusPill: {
    marginTop: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderRadius: 999,
    background: '#f8fafc',
    border: `1px solid ${C.border}`,
    fontSize: 13,
    fontWeight: 500,
    color: C.sub,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#f59e0b',
    animation: 'deebs-pulse 1.4s ease-in-out infinite',
  },
  warningText: {
    marginTop: 12,
    fontSize: 12.5,
    color: C.amber,
  },
  longWaitBox: {
    marginTop: 16,
    padding: '12px 14px',
    borderRadius: 12,
    background: C.amberBg,
    border: '1px solid #fde68a',
    color: C.amber,
    fontSize: 12.5,
    lineHeight: 1.55,
    textAlign: 'left',
  },
  stateWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '20px 0',
  },
  spinner: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '3px solid #eef2f7',
    borderTopColor: C.accent,
    animation: 'deebs-spin .8s linear infinite',
  },
  badgeCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    fontWeight: 700,
  },
  stateTitle: { margin: 0, fontSize: 16, fontWeight: 700 },
  stateText: { margin: 0, fontSize: 13.5, color: C.sub, maxWidth: 300, lineHeight: 1.5 },
  footer: { marginTop: 20, fontSize: 12, color: '#94a3b8' },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.sub,
    marginBottom: 6,
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    fontSize: 14,
    boxSizing: 'border-box',
  },
  primaryBtn: {
    width: '100%',
    marginTop: 12,
    padding: '11px 14px',
    borderRadius: 10,
    border: 'none',
    background: C.text,
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: C.accent,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  pairingCode: {
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: '0.12em',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    padding: '14px 20px',
    borderRadius: 12,
    background: '#f8fafc',
    border: `1px solid ${C.border}`,
  },
}
